import React, { useMemo, useState } from 'react';
import './CompetitorComparison.scss';
import { useTranslation } from 'react-i18next';
import type { InsightsBrandFunnelsResponse } from '@brand24-original/llm-types';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { SortAltIcon } from 'primereact/icons/sortalt';
import { SortAmountDownIcon } from 'primereact/icons/sortamountdown';
import { SortAmountUpAltIcon } from 'primereact/icons/sortamountupalt';
import { AllBrandsStatsResponse } from '../../../../shared/models/projects/stats/AllBrandsStatsResponse';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import BrandIcon from '../../../../shared/components/brand-icon/BrandIcon';
import { brandColorOf } from '../../../../shared/components/report/brandColors';
import { percentOf } from '../../helpers/overviewInsightHelpers';
import { useBrandSelection } from '../../hooks/useBrandSelection';
import BrandPicker from '../brand-picker/BrandPicker';
import GapBar from '../gap-bar/GapBar';
import GapLegend from '../gap-legend/GapLegend';

interface CompetitorComparisonProps {
  data: InsightsBrandFunnelsResponse | null;
  /** SoV per brand (getAllBrandsStats, sovPerPrompt) — source of the visibility rule in the selector. */
  sovBrands: AllBrandsStatsResponse[] | null;
  isLoading: boolean;
  isError?: boolean;
}

interface ComparisonRow {
  brandKey: string;
  label: string;
  color: string;
  isFocus: boolean;
  visibilityPct: number;
  /** null = the brand's domains are unknown (dash), distinct from a measured 0%. */
  citedPct: number | null;
  gapPp: number | null;
}

type SortKey = 'visibilityPct' | 'citedPct' | 'gapPp';
type SortDir = 'asc' | 'desc';

const SORTABLE_COLUMNS: Array<{ key: SortKey; labelKey: string }> = [
  { key: 'visibilityPct', labelKey: 'insightPage.competitorComparison.colVisibility' },
  { key: 'citedPct', labelKey: 'insightPage.competitorComparison.colCited' },
  { key: 'gapPp', labelKey: 'insightPage.competitorComparison.colGap' },
];

/**
 * Sort indicator as in the sortable headers of the overview tables (PrimeReact DataTable defaults):
 * neutral double arrow on every sortable column, direction icon on the active one.
 */
const SortIndicator: React.FC<{ active: boolean; dir: SortDir }> = ({ active, dir }) => {
  if (!active) return <SortAltIcon />;
  return dir === 'asc' ? <SortAmountUpAltIcon /> : <SortAmountDownIcon />;
};

/**
 * "Competitor comparison" (spec §4): the selected brands side by side — share of all responses in
 * scope where the brand is visible vs. where one of its domains is cited (`cited` from the SAME
 * brand-funnels payload as "Funnel vs competitors"), plus the gap in pp. Same response-level
 * denominator as the rest of the tab. Rows use the shared `GapBar` (visibility over cited) and the card ends
 * with the shared `GapLegend`. Numeric columns sort client-side; the rank column follows the current
 * order; an unknown cited (brand without known domains) shows a dash and sorts LAST in both directions.
 * The grid carries ARIA table roles + `aria-sort`, since it is not a DataTable. Brand selection = own
 * `useBrandSelection` instance (independent from the other sections, as decided for
 * FunnelVsCompetitors / the matrix).
 */
const CompetitorComparison: React.FC<CompetitorComparisonProps> = ({
  data,
  sovBrands,
  isLoading,
  isError,
}) => {
  const { t } = useTranslation();
  const { focusBrandKey, pickerBrands, selectedKeys, selectedBrands, brandColors, selectBrands } =
    useBrandSelection(data, sovBrands);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'visibilityPct',
    dir: 'desc',
  });

  const total = data?.totalResponses ?? 0;

  // A new column starts descending (biggest first, as the default visibility order); clicking the
  // active column flips the direction.
  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }
    );

  const rows = useMemo<ComparisonRow[]>(() => {
    const unsorted = selectedBrands.map((brand, index) => {
      const isFocus = brand.brandKey === focusBrandKey;
      const visibilityPct = percentOf(brand.visible, total);
      // `typeof` rather than `=== null`: right after a deploy the StatsCache may still serve the previous
      // payload shape without `cited` for its TTL — `undefined` must read as unknown, not as NaN%.
      const citedPct = typeof brand.cited === 'number' ? percentOf(brand.cited, total) : null;
      return {
        brandKey: brand.brandKey,
        label: brand.label,
        color: brandColorOf(brandColors, brand.label, isFocus, index),
        isFocus,
        visibilityPct,
        citedPct,
        gapPp: citedPct === null ? null : visibilityPct - citedPct,
      };
    });
    const sign = sort.dir === 'asc' ? 1 : -1;
    // Unknown values sit at the BOTTOM in both directions — a fixed sentinel like -1 would jump to the
    // top of an ascending sort, above a measured 0%.
    const unknown = sort.dir === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    const valueOf = (row: ComparisonRow) => row[sort.key] ?? unknown;
    return unsorted.sort(
      (a, b) =>
        sign * (valueOf(a) - valueOf(b)) ||
        b.visibilityPct - a.visibilityPct ||
        a.label.localeCompare(b.label)
    );
  }, [selectedBrands, focusBrandKey, brandColors, total, sort]);

  const hasData = !!data && total > 0 && data.brands.length > 0;
  const hasCited = rows.some((row) => row.citedPct !== null);

  const ariaSortOf = (key: SortKey): 'ascending' | 'descending' | 'none' =>
    sort.key !== key ? 'none' : sort.dir === 'asc' ? 'ascending' : 'descending';

  return (
    <div className="stats__panel competitor-comparison">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('insightPage.competitorComparison.title')}
          <CustomTooltip tooltipText="insightPage.competitorComparison.tooltip" addMargin={false} />
        </div>
        {!isLoading && !isError && hasData && (
          <BrandPicker
            brands={pickerBrands}
            focusBrandKey={focusBrandKey}
            selectedKeys={selectedKeys}
            brandColors={brandColors}
            onChange={selectBrands}
          />
        )}
      </div>
      {isLoading ? (
        <Skeleton className="skeleton--table">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <div className="competitor-comparison__card">
          {isError ? (
            <EmptyPanel textKey="somethingWentWrong" />
          ) : !hasData ? (
            <EmptyPanel />
          ) : (
            <>
              <div className="competitor-comparison__table" role="table">
                <div className="competitor-comparison__header" role="row">
                  <span role="columnheader">#</span>
                  <span role="columnheader">{t('insightPage.competitorComparison.colBrand')}</span>
                  <span role="columnheader" />
                  {SORTABLE_COLUMNS.map(({ key, labelKey }) => (
                    <span key={key} role="columnheader" aria-sort={ariaSortOf(key)}>
                      <Button
                        type="button"
                        className="btn btn--link competitor-comparison__sort"
                        onClick={() => toggleSort(key)}
                      >
                        {t(labelKey)}
                        <SortIndicator active={sort.key === key} dir={sort.dir} />
                      </Button>
                    </span>
                  ))}
                </div>
                {rows.map((row, index) => (
                  <div
                    key={row.brandKey}
                    role="row"
                    className={`competitor-comparison__row${
                      row.isFocus ? ' competitor-comparison__row--focus' : ''
                    }`}
                  >
                    <span role="cell" className="competitor-comparison__rank">
                      {index + 1}
                    </span>
                    <span role="cell" className="competitor-comparison__brand">
                      <BrandIcon name={row.label} color={row.color} size="xs" />
                      <span
                        className={`competitor-comparison__name${
                          row.isFocus ? ' competitor-comparison__name--focus' : ''
                        }`}
                        title={row.label}
                      >
                        {row.label}
                      </span>
                    </span>
                    <span role="cell">
                      <GapBar visibilityPct={row.visibilityPct} citedPct={row.citedPct} />
                    </span>
                    <span role="cell" className="competitor-comparison__value" data-label={t('insightPage.competitorComparison.colVisibility')}>
                      {row.visibilityPct}%
                    </span>
                    <span role="cell" className="competitor-comparison__value" data-label={t('insightPage.competitorComparison.colCited')}>
                      {row.citedPct !== null ? `${row.citedPct}%` : '—'}
                    </span>
                    <span
                      role="cell"
                      className="competitor-comparison__value competitor-comparison__value--gap" data-label={t('insightPage.competitorComparison.colGap')}
                    >
                      {row.gapPp !== null
                        ? t('insightPage.competitorComparison.gapValue', { value: row.gapPp })
                        : '—'}
                    </span>
                  </div>
                ))}
              </div>
              <GapLegend
                visibilityLabel={t('insightPage.competitorComparison.legendVisibility')}
                citedLabel={hasCited ? t('insightPage.competitorComparison.legendCited') : null}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CompetitorComparison;
